import { useLayoutEffect, useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { useTranslation } from "react-i18next"

import useAppStore from "../../../store/useAppStore"
import useLayoutStore from "../../../store/useLayoutStore"
import LoadingSpinner from "../../elements/LoadingSpinner"
import AdminEmployeeMonthLogsModal from "./AdminEmployeeMonthLogsModal"
import AdminStatsMonthReportModal from "./AdminStatsMonthReportModal"

const MAX_SCALE_HOURS = 200
const LOG_ROW_FLASH_MS = 520
const ROW_FADE_OUT_MS = 120
const ROW_STAGGER_WINDOW_MS = 220

const getRowFadeInDelayMs = (index, total) => {
    if (total <= 1 || index <= 0) return 0
    const progress = index / (total - 1)
    const eased = 1 - Math.pow(1 - progress, 2.6)
    return Math.round(eased * ROW_STAGGER_WINDOW_MS)
}

const normalizeSearchText = (value) =>
    String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()

const getFieldMatchScore = (fieldValue, query) => {
    const field = normalizeSearchText(fieldValue)
    if (!field || !query) return 0

    if (field === query) return 100
    if (field.startsWith(query)) return 80 + Math.min(15, (query.length / field.length) * 15)
    if (field.includes(query)) return 50 + Math.min(20, (query.length / field.length) * 20)

    const tokens = field.split(/\s+/)
    let bestTokenScore = 0
    for (const token of tokens) {
        if (token === query) bestTokenScore = Math.max(bestTokenScore, 90)
        else if (token.startsWith(query)) bestTokenScore = Math.max(bestTokenScore, 70)
        else if (token.includes(query)) bestTokenScore = Math.max(bestTokenScore, 40)
    }

    return bestTokenScore
}

const getStatsNameSearchScore = (row, rawQuery) => {
    const query = normalizeSearchText(rawQuery)
    if (!query) return 0

    const fullName = `${row.firstName || ""} ${row.lastName || ""}`.trim()
    const fields = [fullName, row.firstName, row.lastName]

    let best = 0
    let matchedFields = 0
    for (const field of fields) {
        const score = getFieldMatchScore(field, query)
        if (score > 0) matchedFields += 1
        best = Math.max(best, score)
    }

    return best + matchedFields * 2
}

const fieldMatchesSearch = (fieldValue, rawQuery) => {
    const query = normalizeSearchText(rawQuery)
    if (!query) return false
    return getFieldMatchScore(fieldValue, query) > 0
}

const AdminStatsView = ({ employeeSearch = "" }) => {
    const { t } = useTranslation("stats")
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme)
    const isTouchDevice = useLayoutStore((state) => state.isTouchDevice)
    const statsDateFilter = useAppStore((state) => state.statsDateFilter)
    const ensureStatsMonthData = useAppStore((state) => state.ensureStatsMonthData)
    const loadingKarnetData = useAppStore((state) => state.loadingKarnetData)
    const loadingCalendarData = useAppStore((state) => state.loadingCalendarData)
    const karnetData = useAppStore((state) => state.karnetData)
    const employeesData = useAppStore((state) => state.employeesData)
    const openEmployeeMonthLogsModal = useAppStore((state) => state.openEmployeeMonthLogsModal)
    const showStatsMonthReport = useAppStore((state) => state.showStatsMonthReport)
    const statsRowFlashUserId = useAppStore((state) => state.statsRowFlashUserId)
    const statsRowFlashTick = useAppStore((state) => state.statsRowFlashTick)
    const [hoveredLegend, setHoveredLegend] = useState(null)
    const [flashingRowId, setFlashingRowId] = useState(null)
    const [displayedRows, setDisplayedRows] = useState([])
    const [rowsOpaque, setRowsOpaque] = useState(true)
    const [chartAnimKey, setChartAnimKey] = useState(0)
    const prevSearchRef = useRef(null)
    const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL

    const monthKey = `${statsDateFilter.year}-${String(statsDateFilter.month + 1).padStart(2, "0")}`

    useEffect(() => {
        setFlashingRowId(null)
    }, [monthKey])

    useEffect(() => {
        if (!statsRowFlashUserId) return undefined
        setFlashingRowId(String(statsRowFlashUserId))
        const timer = window.setTimeout(() => setFlashingRowId(null), LOG_ROW_FLASH_MS)
        return () => window.clearTimeout(timer)
    }, [statsRowFlashUserId, statsRowFlashTick])

    useLayoutEffect(() => {
        ensureStatsMonthData(statsDateFilter.month, statsDateFilter.year)
    }, [statsDateFilter.month, statsDateFilter.year, ensureStatsMonthData])

    const hourSteps = useMemo(() => (
        Array.from({ length: 20 }, (_, idx) => (idx + 1) * 10)
    ), [])

    const workHoursMarkers = useMemo(() => {
        const year = statsDateFilter.year
        const month = statsDateFilter.month
        const daysInMonth = new Date(year, month + 1, 0).getDate()

        let workingDays = 0
        for (let day = 1; day <= daysInMonth; day += 1) {
            const dayOfWeek = new Date(year, month, day).getDay()
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                workingDays += 1
            }
        }

        const hoursAt8 = workingDays * 8
        const hoursAt7_5 = workingDays * 7.5

        return {
            workingDays,
            hoursAt8,
            hoursAt7_5,
            line8Percent: Math.min((hoursAt8 / MAX_SCALE_HOURS) * 100, 100),
            line7_5Percent: Math.min((hoursAt7_5 / MAX_SCALE_HOURS) * 100, 100)
        }
    }, [statsDateFilter.month, statsDateFilter.year])

    const sortedRows = useMemo(() => {
        if (!karnetData) return []

        const list = Object.entries(karnetData).map(([userId, user]) => {
            const totalMinutes = Object.values(user?.days || {}).reduce((sum, value) => {
                return sum + (Number(value) || 0)
            }, 0)
            const totalHours = totalMinutes / 60
            const hoursPart = Math.floor(totalMinutes / 60)
            const minutesPart = totalMinutes % 60
            const exactTime = `${String(hoursPart).padStart(2, "0")}:${String(minutesPart).padStart(2, "0")}`
            const employee = employeesData?.[userId]

            return {
                userId,
                firstName: user?.ime || "",
                lastName: user?.prezime || "",
                image: employee?.image || user?.image || "",
                totalHours,
                exactTime
            }
        })

        const query = employeeSearch.trim()
        if (!query) {
            return list.sort((a, b) => {
                const aName = `${a.firstName} ${a.lastName}`.trim()
                const bName = `${b.firstName} ${b.lastName}`.trim()
                return aName.localeCompare(bName, "sr", { sensitivity: "base" })
            })
        }

        return list
            .map((row) => ({
                row,
                score: getStatsNameSearchScore(row, query),
            }))
            .sort((a, b) => {
                if (b.score !== a.score) return b.score - a.score
                const aName = `${a.row.firstName} ${a.row.lastName}`.trim()
                const bName = `${b.row.firstName} ${b.row.lastName}`.trim()
                return aName.localeCompare(bName, "sr", { sensitivity: "base" })
            })
            .map(({ row }) => row)
    }, [karnetData, employeesData, employeeSearch])

    useEffect(() => {
        if (prevSearchRef.current === null) {
            prevSearchRef.current = employeeSearch
            setDisplayedRows(sortedRows)
            setRowsOpaque(true)
            return
        }

        if (prevSearchRef.current === employeeSearch) {
            setDisplayedRows(sortedRows)
            return
        }

        let cancelled = false
        setRowsOpaque(false)

        const timeoutId = setTimeout(() => {
            if (cancelled) return
            prevSearchRef.current = employeeSearch
            setDisplayedRows(sortedRows)
            setChartAnimKey((key) => key + 1)
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!cancelled) setRowsOpaque(true)
                })
            })
        }, ROW_FADE_OUT_MS)

        return () => {
            cancelled = true
            clearTimeout(timeoutId)
        }
    }, [employeeSearch, sortedRows])

    const getCellAnimStyle = (index) => {
        const delayMs = rowsOpaque
            ? getRowFadeInDelayMs(index, displayedRows.length)
            : 0
        return {
            opacity: rowsOpaque ? 1 : 0,
            transition: `opacity 0.12s ease ${delayMs}ms`,
        }
    }

    return (
        <>
            <AnimatePresence mode="wait">
                {(loadingKarnetData || loadingCalendarData) ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        <motion.div
                            key={monthKey}
                            className="asv-stats-month-block"
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, }}
                            transition={{ duration: 0.19, ease: "easeOut" }}
                        >
                            <div className="asv-stats-scroll-inner">
                                <div
                                    className={`asv-target-hours ${isDarkTheme ? "asv-target-hours--dark" : ""}`}
                                    aria-hidden
                                >
                                    <div className="asv-target-hours-spacer asv-target-hours-spacer--row-num" />
                                    <div className="asv-target-hours-spacer asv-target-hours-spacer--avatar" />
                                    <div className="asv-target-hours-spacer" />
                                    <div className="asv-target-hours-spacer" />
                                    <div className="asv-target-hours-scale">
                                        <span
                                            className="asv-target-hours-item asv-target-hours-item--7-5"
                                            style={{ left: `${workHoursMarkers.line7_5Percent}%` }}
                                        >
                                            {workHoursMarkers.hoursAt7_5}h
                                        </span>
                                        <span
                                            className="asv-target-hours-item asv-target-hours-item--8"
                                            style={{ left: `${workHoursMarkers.line8Percent}%` }}
                                        >
                                            {workHoursMarkers.hoursAt8}h
                                        </span>
                                    </div>
                                </div>
                                <div className="kv-table-container asv-table-container">
                                    <table className={`kv-table asv-table ${isDarkTheme ? "dark-table" : ""}`}>
                                        <thead>
                                            <tr>
                                                <th className="st-row-num-col">{t("admin.columns.number")}</th>
                                                <th className="st-avatar-col"> </th>
                                                <th>{t("admin.columns.firstName")}</th>
                                                <th>{t("admin.columns.lastName")}</th>
                                                {hourSteps.map((step) => (
                                                    <th key={step}>{step}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {displayedRows.map((row, idx) => (
                                                <AdminStatsRow
                                                    key={row.userId}
                                                    row={row}
                                                    idx={idx}
                                                    hourSteps={hourSteps}
                                                    isDarkTheme={isDarkTheme}
                                                    baseImgUrl={baseImgUrl}
                                                    employeeSearch={employeeSearch}
                                                    workHoursMarkers={workHoursMarkers}
                                                    chartAnimKey={chartAnimKey}
                                                    cellAnimStyle={getCellAnimStyle(idx)}
                                                    isFlashing={String(row.userId) === String(flashingRowId)}
                                                    onRowClick={() => openEmployeeMonthLogsModal(row.userId)}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </motion.div>
                        <div className="asv-footer">
                            <button
                                type="button"
                                className={`cv-new-absence-button asv-month-report-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                                onClick={() => showStatsMonthReport()}
                            >
                                {t("admin.monthReportButton")}
                            </button>
                            <div className={`asv-legend ${isDarkTheme ? "asv-legend-dark" : ""}`}>
                                <span
                                    className="asv-legend-item"
                                    onMouseEnter={() => !isTouchDevice && setHoveredLegend("effective")}
                                    onMouseLeave={() => !isTouchDevice && setHoveredLegend(null)}
                                >
                                    <span className="asv-legend-dot asv-legend-dot-7-5" />
                                    {t("admin.legend.effectiveWork")}
                                    <AnimatePresence>
                                        {hoveredLegend === "effective" && (
                                            <motion.div
                                                key="legend-effective"
                                                className={`v-header-button-tooltip asv-legend-tooltip ${isDarkTheme ? "tooltip-dark" : "tooltip-white"}`}
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.18, ease: "easeOut" }}
                                            >
                                                {t("admin.legend.effectiveTooltip", {
                                                    count: workHoursMarkers.workingDays,
                                                    hours: workHoursMarkers.hoursAt7_5
                                                })}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </span>
                                <span
                                    className="asv-legend-item"
                                    onMouseEnter={() => !isTouchDevice && setHoveredLegend("presence")}
                                    onMouseLeave={() => !isTouchDevice && setHoveredLegend(null)}
                                >
                                    <span className="asv-legend-dot asv-legend-dot-8" />
                                    {t("admin.legend.totalPresence")}
                                    <AnimatePresence>
                                        {hoveredLegend === "presence" && (
                                            <motion.div
                                                key="legend-presence"
                                                className={`v-header-button-tooltip asv-legend-tooltip ${isDarkTheme ? "tooltip-dark" : "tooltip-white"}`}
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.18, ease: "easeOut" }}
                                            >
                                                {t("admin.legend.presenceTooltip", {
                                                    count: workHoursMarkers.workingDays,
                                                    hours: workHoursMarkers.hoursAt8
                                                })}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </AnimatePresence>
            <AdminEmployeeMonthLogsModal />
            <AdminStatsMonthReportModal />
        </>
    )
}

const AdminStatsRow = ({
    row,
    idx,
    hourSteps,
    isDarkTheme,
    baseImgUrl,
    employeeSearch,
    workHoursMarkers,
    chartAnimKey,
    cellAnimStyle,
    isFlashing,
    onRowClick,
}) => {
    const widthPercent = Math.max(0, Math.min((row.totalHours / MAX_SCALE_HOURS) * 100, 100))
    const firstNameMatch = fieldMatchesSearch(row.firstName, employeeSearch)
    const lastNameMatch = fieldMatchesSearch(row.lastName, employeeSearch)

    return (
        <tr
            className={`asv-row asv-emp-day-log-row${isFlashing ? " asv-emp-day-log-row--flash" : ""}`}
            onClick={onRowClick}
        >
            <td className="st-row-num-col" style={cellAnimStyle}>{idx + 1}</td>
            <td className="st-avatar-col" style={cellAnimStyle}>
                <img
                    src={`${baseImgUrl}/employees/${row.image}`}
                    alt=""
                    className={`st-employee-avatar ${isDarkTheme ? "dark-border" : ""}`}
                    crossOrigin="use-credentials"
                    onError={(e) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = "/views/login.svg"
                    }}
                />
            </td>
            <td
                className={`asv-name-cell ${isDarkTheme ? "dark-color" : ""}${firstNameMatch ? " st-cell-match" : ""}`}
                style={cellAnimStyle}
            >
                {row.firstName}
            </td>
            <td
                className={`asv-name-cell ${isDarkTheme ? "dark-color" : ""}${lastNameMatch ? " st-cell-match" : ""}`}
                style={cellAnimStyle}
            >
                {row.lastName}
            </td>
            <td className="asv-bar-cell" colSpan={hourSteps.length} style={cellAnimStyle}>
                <div className="asv-bar-track">
                    <div className="asv-target-line asv-target-line-7-5" style={{ left: `${workHoursMarkers.line7_5Percent}%` }} />
                    <div className="asv-target-line asv-target-line-8" style={{ left: `${workHoursMarkers.line8Percent}%` }} />
                    <motion.div
                        key={chartAnimKey}
                        className="asv-bar-fill"
                        style={{ width: `${widthPercent}%` }}
                        initial={{ scaleX: 0, originX: 0 }}
                        animate={{ scaleX: 1, originX: 0 }}
                        transition={{ duration: 0.28, ease: "easeOut", delay: idx * 0.015 }}
                    />
                    <span className={`asv-bar-time-label asv-bar-time-label-hover ${isDarkTheme ? "dark-color" : ""}`}>
                        {row.exactTime}
                    </span>
                </div>
            </td>
        </tr>
    )
}

export default AdminStatsView
